import React from "react";
import {
  Autocomplete,
  Button,
  createFilterOptions,
  FormControl,
  TextField,
} from "@mui/material";

import ModalContainer from "components/common/modals/modalContainer/ModalContainer";
import { PATIENT_DEACTIVATE_REASONS } from "static/constants";
import { useDispatch } from "react-redux";
import {
  changeProfileField,
  toggleDeactivate,
} from "app/store/transmission/slice/transmissionSlice";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import { Formik } from "formik";
import { deactivatePatientValidationSchema } from "schemas/deactivatePatientValidationSchema";
import {
  patientApi,
  useUpdatePatientMutation,
} from "app/store/patient/api/patientApi";
import { showErrorToast, showSuccessToast } from "utils/toast";

const DeactivatePatientModal = () => {
  const dispatch = useDispatch();
  const { profile, isDeactivate, deactivateFrom, patientId } = useSelector(
    (state) => state.transmission
  );

  const autoCompleteFilter = createFilterOptions();
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  const handleCancel = (reason) => {
    dispatch(
      toggleDeactivate({
        isDeactivate: false,
        reason: reason || "",
      })
    );
  };

  const handleConfirm = async (reason) => {
    let profileCopy = cloneDeep(profile);
    profileCopy["deactivate_reason"] = reason;

    if (deactivateFrom && deactivateFrom === "table") {
      try {
        const response = await updatePatient({
          patientId,
          payload: {
            active: !isDeactivate,
            deactivate_reason: reason,
          },
        }).unwrap();
        if (!response?.error) {
          showSuccessToast(response?.message);
          dispatch(patientApi.util.invalidateTags(["patientList"]));
        }
      } catch (error) {
        showErrorToast(error?.message);
      }
    } else {
      dispatch(
        changeProfileField({
          type: "profile",
          value: profileCopy,
        })
      );
    }
    handleCancel(reason);
  };

  return (
    <>
      <ModalContainer
        show={isDeactivate}
        onClose={() => handleCancel()}
        title="Reason For Deactivating Patient"
      >
        <Formik
          initialValues={{ reason: profile?.deactivate_reason }}
          validationSchema={deactivatePatientValidationSchema}
          onSubmit={(values) => handleConfirm(values?.reason)}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            handleBlur,
          }) => (
            <>
              <FormControl fullWidth required margin="normal">
                <Autocomplete
                  freeSolo
                  id="reason"
                  name="reason"
                  value={values?.reason}
                  options={
                    PATIENT_DEACTIVATE_REASONS?.map((option) => ({
                      label: option,
                      inputValue: option,
                    })) || []
                  }
                  openOnFocus={true}
                  onBlur={handleBlur}
                  onChange={(e, newValue) => {
                    const selectedReason =
                      typeof newValue === "string"
                        ? newValue
                        : newValue?.inputValue;

                    const copy = cloneDeep(profile);
                    copy.deactivateReason = selectedReason;

                    setFieldValue("reason", selectedReason?.trim() || "");
                  }}
                  filterOptions={(options, params) => {
                    const filtered = autoCompleteFilter(options, params);

                    if (params.inputValue !== "") {
                      filtered.unshift({
                        inputValue: params.inputValue,
                        label: `+ Add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      multiline
                      value={values?.reason}
                      variant="outlined"
                      label="Deactivate Reason"
                      placeholder="Deactivate Reason"
                      error={touched.reason && Boolean(errors.reason)}
                      helperText={touched.reason && errors.reason}
                    />
                  )}
                  getOptionLabel={(option) => {
                    return option?.inputValue || option.label || option;
                  }}
                  renderOption={(props, option) => (
                    <li {...props} style={{ padding: "8px 16px" }}>
                      {option?.label}
                    </li>
                  )}
                />
              </FormControl>

              <div className="d-flex flex-end align-items-center g-1 mt-2">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleCancel()}
                  disabled={isSubmitting || isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmit}
                  disabled={!values?.reason || isSubmitting || isLoading}
                >
                  Confirm
                </Button>
              </div>
            </>
          )}
        </Formik>
      </ModalContainer>
    </>
  );
};

export default DeactivatePatientModal;
